import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import FooterHelpText from '@/components/FooterHelpText/FooterHelpText';
import Layout from '@/components/layout';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { LocationViewChoices, PricingPlan } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { pricingLimitLocation } from '@/helpers';
import { useGetGeneralSettingQuery, useGetLocationsQuery } from '@/redux/query';
import { locationTabSelector } from '@/redux/slice/tab.slice';
import { IListLocation } from '@/types/components/location';
import { BlockStack, Box, Button, InlineStack } from '@shopify/polaris';
import { useSelector } from 'react-redux';
import TableLocations from './component/IndexTableLocations/TableLocations';
import SkeletonTableLocations from './component/SkeletonTableLocations/SkeletonTableLocations';

export default function LocationPage() {
  const { t } = useTranslation('locations');
  const navigate = useNavigate();
  const locationTab = useSelector(locationTabSelector);
  const [searchParams] = useSearchParams();
  const {
    data: locationData,
    isLoading: isLocationLoading,
    refetch: refetchLocationData,
  } = useGetLocationsQuery({ shop: config.shop });
  const { data: generalSettingData, isLoading: isLoadingGeneralSettingData } = useGetGeneralSettingQuery({ shop: config.shop });

  const listLocations = useMemo(() => {
    return locationData?.data ? (JSON.parse(JSON.stringify(locationData.data)) as IListLocation[]) : [];
  }, [locationData?.data]);

  const selectedView = useMemo(() => locationTab || LocationViewChoices.ALL, [locationTab]);

  const handleAddLocation = useCallback(
    () => navigate({ pathname: `${PATH.LOCATIONS.pathname}/new`, search: searchParams.toString() }),
    [navigate, searchParams],
  );

  const primaryAction = useMemo(() => {
    return listLocations.length > 0 ? (
      <InlineStack blockAlign="center" gap={'200'}>
        {generalSettingData?.data.plan === PricingPlan.Enterprise &&
          listLocations.length >= pricingLimitLocation(generalSettingData?.data.plan || '') && (
            <RegularText>You have reached the maximum number of locations.</RegularText>
          )}
        {generalSettingData?.data.plan !== PricingPlan.Enterprise &&
          listLocations.length >= pricingLimitLocation(generalSettingData?.data.plan || '') && (
            <Box>
              This plan has a limited number of locations. Please{' '}
              <Button variant="plain" onClick={() => navigate(PATH.PRICING.pathname)}>
                upgrade to a higher plan
              </Button>{' '}
              to add more locations.
            </Box>
          )}
        <Button
          onClick={handleAddLocation}
          variant="primary"
          disabled={listLocations.length >= pricingLimitLocation(generalSettingData?.data.plan || '')}
        >
          {t('tableLocation.addLocation')}
        </Button>
      </InlineStack>
    ) : undefined;
  }, [generalSettingData?.data.plan, handleAddLocation, listLocations.length, navigate, t]);

  const isShowContact = useMemo(() => {
    switch (selectedView) {
      case LocationViewChoices.PICKUP:
        return listLocations.filter((item) => item.isEnablePickup)?.length > 0;
      case LocationViewChoices.DELIVERY:
        return listLocations.filter((item) => item.isEnableDelivery)?.length > 0;
      default:
        return listLocations?.length > 0;
    }
  }, [selectedView, listLocations]);

  return (
    <Layout title={t('tableLocation.locations')} primaryAction={primaryAction}>
      {isLocationLoading || isLoadingGeneralSettingData ? (
        <SkeletonTableLocations />
      ) : (
        <BlockStack gap="400">
          <TableLocations listLocations={listLocations} selectedView={selectedView} refetchLocation={refetchLocationData} />
        </BlockStack>
      )}
      <FooterHelpText linkDocs="https://docs.dingdoong.io/dashboard/dashboard" isShowContact={isShowContact} />
    </Layout>
  );
}
